import { useState, useEffect, useCallback, useRef } from 'react';
import { constant, noop } from 'lodash-es';

/**
 * @typedef {object} ValidateSuccessPayload
 * @property {string} value - The value that was successfully validated.
 * @property {boolean} didChange - `true` if async validation ran, `false` if the value was unchanged and validation was skipped.
 */

/**
 * @typedef {object} ValidatedInputApi
 * @property {boolean} isValid - The current validity state of the input.
 * @property {object} inputProps - Props to be spread onto an <input> element.
 * @property {string} inputProps.value - The internal value of the input.
 * @property {function(React.ChangeEvent<HTMLInputElement>): void} inputProps.onChange - The change handler for the input.
 * @property {function(): Promise<void>} validate - The function to manually trigger validation.
 */

/**
 * A hook to manage input state with synchronous and asynchronous validation.
 * It maintains an internal value to allow free user input, and syncs with an
 * external `value` prop when it changes.
 *
 * @param {object} props
 * @param {string} [props.value=''] - The external "source of truth" value.
 * @param {function(string): boolean} [props.onSyncValidate] - Synchronous validation function. Must return `true` or `false`.
 * @param {function(string): Promise<void>} [props.onAsyncValidate] - Asynchronous validation function (e.g., API call). Should throw an error or return a rejected promise on failure.
 * @param {function(): void} [props.onBeforeValidate] - Callback invoked at the start of the `validate` call.
 * @param {function(): void} [props.onAfterValidate] - Callback invoked at the end of the `validate` call (in `finally`).
 * @param {function(ValidateSuccessPayload): void} [props.onValidateSuccess] - Callback invoked only on a successful validation (both sync and async).
 *
 * @returns {ValidatedInputApi}
 */

export function useValidatedInput({
  value = '',
  onSyncValidate = constant(true),
  onAsyncValidate = async () => {},
  onBeforeValidate = noop,
  onAfterValidate = noop,
  onValidateSuccess = noop,
}) {
  const [internalValue, setInternalValue] = useState(value);
  const [isValid, setIsValid] = useState(true);

  const valueRef = useRef(value);

  useEffect(() => {
    valueRef.current = internalValue;
  }, [internalValue]);

  useEffect(() => {
    if (value !== valueRef.current) {
      setInternalValue(value);
      setIsValid(true);
    }
  }, [value]);

  const lastSubmittedValueRef = useRef(value);

  const triggerValidate = useCallback(async () => {
    const valueToUpdate = valueRef.current;

    if (!onSyncValidate(valueToUpdate)) {
      setIsValid(false);
      return;
    }

    setIsValid(true);

    if (valueToUpdate === lastSubmittedValueRef.current) {
      onBeforeValidate();
      onValidateSuccess({ value: valueToUpdate, didChange: false });
      onAfterValidate();
      return;
    }

    onBeforeValidate();

    try {
      await onAsyncValidate(valueToUpdate);
      lastSubmittedValueRef.current = valueToUpdate;
      onValidateSuccess({ value: valueToUpdate, didChange: true });
    } catch {
      setIsValid(false);
    } finally {
      onAfterValidate();
    }
  }, [onAfterValidate, onAsyncValidate, onBeforeValidate, onSyncValidate, onValidateSuccess]);

  const handleChange = useCallback(
    e => {
      setInternalValue(e.target.value);
      if (!isValid) {
        setIsValid(true);
      }
    },
    [isValid]
  );

  return {
    isValid,
    inputProps: { value: internalValue, onChange: handleChange },
    validate: triggerValidate,
  };
}
